/**
 * RecipeSelector Component
 *
 * Dropdown to select an existing recipe template or create a new one.
 * Displays recipe templates filtered by type and strain.
 *
 * US-P2-010: Process Recipe Integration - Task 3
 */

import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Plus, Package, AlertCircle } from 'lucide-react'
import { useRecipeTemplates, RECIPE_TYPE_LABELS } from '@/hooks/useRecipeTemplates'

/**
 * RecipeSelector - Select or create a recipe template
 *
 * @param {string} recipeType - Filter by type ('block', 'spawn', 'liquid_culture', 'other')
 * @param {string} strainId - Optional strain ID for strain-specific recipes
 * @param {string} value - Currently selected recipe template ID
 * @param {function} onChange - Callback when selection changes
 * @param {function} onCreateNew - Callback to trigger new recipe creation
 * @param {boolean} disabled - Whether the selector is disabled
 */
export default function RecipeSelector({
  recipeType,
  strainId = null,
  value,
  onChange,
  onCreateNew,
  disabled = false
}) {
  const { templates, loading, error } = useRecipeTemplates(recipeType, strainId)

  // Separate generic and strain-specific templates
  const genericTemplates = templates.filter(t => t.is_generic)
  const strainTemplates = templates.filter(t => !t.is_generic)

  // Find selected template for display
  const selectedTemplate = templates.find(t => t.id === value)

  if (loading) {
    return (
      <div className="h-9 flex items-center text-sm text-muted-foreground">
        <div className="w-4 h-4 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin mr-2" />
        Loading recipes...
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-destructive">
        <AlertCircle className="h-4 w-4" />
        Failed to load recipes
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Select
        value={value || 'none'}
        onValueChange={(v) => onChange(v === 'none' ? null : v)}
        disabled={disabled}
      >
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select a recipe template">
            {selectedTemplate ? (
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span>{selectedTemplate.label}</span>
                {selectedTemplate.is_generic && (
                  <Badge variant="outline" className="text-[10px] h-4 px-1">
                    Generic
                  </Badge>
                )}
              </div>
            ) : (
              <span className="text-muted-foreground">Select a recipe template</span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="none">
            <span className="text-muted-foreground">No recipe selected</span>
          </SelectItem>

          {/* Generic templates first */}
          {genericTemplates.length > 0 && (
            <SelectGroup>
              <SelectLabel className="text-xs text-muted-foreground">
                Generic {RECIPE_TYPE_LABELS[recipeType] || 'Recipes'}
              </SelectLabel>
              {genericTemplates.map(template => (
                <SelectItem key={template.id} value={template.id}>
                  <div className="flex items-center gap-2">
                    <span>{template.name}</span>
                    <Badge variant="outline" className="text-[10px] h-4 px-1">
                      Generic
                    </Badge>
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          )}

          {/* Strain-specific templates */}
          {strainTemplates.length > 0 && (
            <SelectGroup>
              <SelectLabel className="text-xs text-muted-foreground">
                Strain-Specific
              </SelectLabel>
              {strainTemplates.map(template => (
                <SelectItem key={template.id} value={template.id}>
                  <div className="flex items-center gap-2">
                    <span>{template.name}</span>
                    {template.strain_code && (
                      <Badge variant="secondary" className="text-[10px] h-4 px-1">
                        {template.strain_code}
                      </Badge>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectGroup>
          )}

          {/* Empty state */}
          {templates.length === 0 && (
            <div className="py-4 text-center text-sm text-muted-foreground">
              No recipes available for this type
            </div>
          )}
        </SelectContent>
      </Select>

      {/* Create new recipe button */}
      {onCreateNew && (
        <Button
          variant="outline"
          size="sm"
          className="w-full border-dashed text-xs"
          onClick={onCreateNew}
          disabled={disabled}
        >
          <Plus className="h-3 w-3 mr-1" />
          Create New Recipe
        </Button>
      )}
    </div>
  )
}
